import { itemStatusLabel } from '../../utils/orderStatus';
import {
  DINE_IN_ORDER_TYPE,
  type CaptainMessage,
  type CaptainMessageType,
  type OngoingOrderSnapshot,
} from '../types/events';

function formatTime(date = new Date()): string {
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export function buildItemSummary(items: OngoingOrderSnapshot['items']): string {
  if (!items.length) return 'your items';
  const parts = items.slice(0, 3).map((item) => {
    const qty = item.quantity > 1 ? `${item.quantity}× ` : '';
    return `${qty}${item.name}`;
  });
  if (items.length > 3) {
    parts.push(`${items.length - 3} more`);
  }
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

function statusToMessageType(status: string): CaptainMessageType {
  switch (status) {
    case 'ORDER_PLACED':
    case 'ORDER_INITIATED':
    case 'ORDER_MODIFIED':
      return 'placed';
    case 'ORDER_CONFIRMED':
      return 'confirmed';
    case 'ORDER_PREPARING':
      return 'preparing';
    case 'ORDER_PREPARED':
    case 'ORDER_SERVED':
    case 'ORDER_PICKED':
      return 'ready';
    case 'ORDER_COMPLETED':
    case 'DINEIN_ORDER_COMPLETED':
    case 'TAKEAWAY_ORDER_COMPLETED':
    case 'DELIVERY_ORDER_COMPLETED':
      return 'complete';
    case 'ORDER_CANCELLED':
    case 'ORDER_FAILED':
      return 'cancelled';
    default:
      return 'system';
  }
}

const TYPE_LABELS: Record<CaptainMessageType, string> = {
  welcome: 'Welcome',
  placed: 'Order Placed',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready to Serve',
  complete: 'Complete',
  cancelled: 'Cancelled',
  wait_time: 'Wait Time',
  table_ready: 'Table Ready',
  nudge: 'Tip',
  item_status: 'Item Update',
  assistance: 'Request Sent',
  system: 'Update',
};

export function getMessageTypeLabel(type: CaptainMessageType): string {
  return TYPE_LABELS[type] || 'Update';
}

export interface ResolvedCaptainMessage {
  message: CaptainMessage;
  speakText: string;
}

export function resolveWelcomeMessage(
  restaurantName: string,
  appName?: string
): ResolvedCaptainMessage {
  const text = `Welcome to ${restaurantName}! I'm Peppy AI — your dining assistant. I can help you explore the menu, add items to your cart, track your order, and call for assistance. Use the Updates switch if you don't want this chat to open automatically.`;
  return {
    message: {
      id: appName ? `welcome-${appName}` : `welcome-${restaurantName}`,
      type: 'welcome',
      title: 'Welcome',
      text,
      time: formatTime(),
    },
    speakText: text,
  };
}

export function resolveMenuBrowseNudge(appName: string): ResolvedCaptainMessage {
  const text = `Browse the menu and tap any dish to see details, add-ons, and ratings. I can summarize items for you.`;
  return {
    message: {
      id: `nudge-menu_list-${appName}`,
      type: 'nudge',
      title: 'Menu tip',
      text,
      time: formatTime(),
    },
    speakText: text,
  };
}

export function resolvePageNudgeMessage(
  pageKey: string,
  text: string,
  title = 'Peppy AI'
): ResolvedCaptainMessage {
  return {
    message: {
      id: `nudge-${pageKey}`,
      type: 'nudge',
      title,
      text,
      time: formatTime(),
    },
    speakText: text,
  };
}

export function resolveItemStatusMessage(
  orderItemId: number | string,
  itemName: string,
  status: string,
  quantity = 1
): ResolvedCaptainMessage | null {
  const normalized = (status || '').toUpperCase();
  if (!normalized || normalized === 'PLACED' || normalized === 'CONFIRMED') {
    return null;
  }

  const label = itemStatusLabel(normalized);
  const qtyPrefix = quantity > 1 ? `${quantity}× ` : '';
  let text = '';
  let title = 'Item Update';

  switch (normalized) {
    case 'PREPARING':
      text = `${qtyPrefix}${itemName} is being prepared in the kitchen.`;
      title = 'Preparing';
      break;
    case 'READY':
      text = `${qtyPrefix}${itemName} is ready!`;
      title = 'Ready';
      break;
    case 'SERVED':
      text = `${qtyPrefix}${itemName} has been served. Enjoy!`;
      title = 'Served';
      break;
    case 'CANCELLED':
      text = `${qtyPrefix}${itemName} was removed from your order.`;
      title = 'Cancelled';
      break;
    case 'COMPLETED':
      text = `${qtyPrefix}${itemName} is complete.`;
      break;
    default:
      text = `${qtyPrefix}${itemName}: ${label}.`;
  }

  return {
    message: {
      id: `item-${orderItemId}-${normalized}`,
      type: 'item_status',
      title,
      text,
      time: formatTime(),
      animate: true,
    },
    speakText: text,
  };
}

export function resolveOrderStatusMessage(
  status: string,
  order: OngoingOrderSnapshot,
  restaurantName: string
): ResolvedCaptainMessage | null {
  const itemSummary = buildItemSummary(order.items);
  const eta = order.waitTimeMinutes ? `~${order.waitTimeMinutes} min` : undefined;
  const type = statusToMessageType(status);
  const orderRef = order.orderCode || `#${order.orderId}`;

  let text = '';
  let title = getMessageTypeLabel(type);

  switch (status) {
    case 'ORDER_PLACED':
    case 'ORDER_INITIATED':
    case 'ORDER_MODIFIED':
      text = `Order ${orderRef} received! ${itemSummary}. We'll confirm it shortly.`;
      break;
    case 'ORDER_CONFIRMED':
      text = `Your order is confirmed. The kitchen will start preparing soon.`;
      break;
    case 'ORDER_PREPARING':
      text = eta
        ? `Your order is being prepared. Estimated time: ${eta}. I'll ping you when it's ready.`
        : `Your order is being prepared. I'll update you when it's ready.`;
      break;
    case 'ORDER_PREPARED':
    case 'ORDER_SERVED':
      text =
        order.orderType === DINE_IN_ORDER_TYPE
          ? `Your food is on the way to your table! Enjoy every bite.`
          : `${itemSummary} is ready. Please enjoy your meal!`;
      break;
    case 'ORDER_PICKED':
      text = `Your order is packed and ready for pickup.`;
      break;
    case 'ORDER_COMPLETED':
    case 'DINEIN_ORDER_COMPLETED':
    case 'TAKEAWAY_ORDER_COMPLETED':
    case 'DELIVERY_ORDER_COMPLETED':
      text = `Thank you for dining with ${restaurantName}! Hope to see you again soon.`;
      break;
    case 'ORDER_CANCELLED':
    case 'ORDER_FAILED':
      text = `Your order was cancelled. Please contact staff if you need help.`;
      break;
    default:
      return null;
  }

  return {
    message: {
      id: `status-${order.orderId}-${status}`,
      type,
      title,
      text,
      time: formatTime(),
      eta,
      animate: true,
    },
    speakText: text,
  };
}

export function resolveWaitTimeMessage(
  minutes: number,
  orderId?: number
): ResolvedCaptainMessage {
  const text = `Updated wait time: about ${minutes} minutes.`;
  return {
    message: {
      id: orderId != null ? `wait-${orderId}-${minutes}` : `wait-${minutes}`,
      type: 'wait_time',
      title: 'Wait Time',
      text,
      time: formatTime(),
      eta: `~${minutes} min`,
      animate: true,
    },
    speakText: text,
  };
}

export function resolveTableReadyMessage(): ResolvedCaptainMessage {
  const text = `Your table is ready. You can start ordering whenever you like.`;
  return {
    message: {
      id: `table-${Date.now()}`,
      type: 'table_ready',
      title: 'Table Ready',
      text,
      time: formatTime(),
      animate: true,
    },
    speakText: text,
  };
}

export function resolveQueueReadyMessage(tokenLabel?: string): ResolvedCaptainMessage {
  const label = tokenLabel ? `Token ${tokenLabel}` : 'Your token';
  const text = `${label} is ready. Please proceed to the counter or host stand.`;
  return {
    message: {
      id: `queue-ready-${Date.now()}`,
      type: 'table_ready',
      title: 'Queue Ready',
      text,
      time: formatTime(),
      animate: true,
    },
    speakText: text,
  };
}

export function resolveQueueStatusMessage(
  tokenLabel: string,
  status: string,
  position?: number
): ResolvedCaptainMessage {
  let text = `Your queue token is ${tokenLabel}.`;
  if (status === 'WAITING' && position && position > 0) {
    text = `You are number ${position} in line with token ${tokenLabel}.`;
  } else if (status === 'CALLING') {
    text = `Token ${tokenLabel} is being called now. Please proceed.`;
  } else if (status === 'READY') {
    text = `Token ${tokenLabel} is ready. Please proceed.`;
  }
  return {
    message: {
      id: `queue-status-${Date.now()}`,
      type: 'system',
      title: 'Queue Update',
      text,
      time: formatTime(),
      animate: true,
    },
    speakText: text,
  };
}

export function resolveAssistanceMessage(label: string, tableNumber?: number): ResolvedCaptainMessage {
  const tableSuffix = tableNumber != null ? ` for Table ${tableNumber}` : '';
  const text = `${label} request sent${tableSuffix}. Staff has been notified.`;
  return {
    message: {
      id: `assist-${Date.now()}`,
      type: 'assistance',
      title: 'Request Sent',
      text,
      time: formatTime(),
      animate: true,
    },
    speakText: text,
  };
}

export function resolveCallCaptainMessage(tableNumber?: number): ResolvedCaptainMessage {
  const text =
    tableNumber != null
      ? `Captain has been notified for Table ${tableNumber}.`
      : `Captain has been notified and will be with you shortly.`;
  return {
    message: {
      id: `call-${Date.now()}`,
      type: 'assistance',
      title: 'Captain Called',
      text,
      time: formatTime(),
      animate: true,
    },
    speakText: text,
  };
}
